<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  charge: { type: Object, required: true },
  downloading: { type: Boolean, default: false },
});
const emit = defineEmits(['download']);
const { t } = useI18n();

const installment = computed(() => props.charge.dados_parcela || {});
const commission = computed(() => props.charge.dados_comissao_geral || {});
const canDownload = computed(
  () =>
    Boolean(installment.value.planilha_id) &&
    props.charge.acoes?.pode_baixar_planilha
);
const formatMoney = value => {
  const amount = Number(value?.amount);
  if (!Number.isFinite(amount)) return t('SALES.PENDING_CHARGE.EMPTY_VALUE');
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: value.currency || 'BRL',
  }).format(amount);
};
const periodLabels = computed(() => ({
  assinatura_recibo_sinal: t(
    'SALES.PENDING_CHARGE.PAYMENT_PERIODS.ASSINATURA_RECIBO_SINAL'
  ),
  retirada_certidoes: t(
    'SALES.PENDING_CHARGE.PAYMENT_PERIODS.RETIRADA_CERTIDOES'
  ),
  ato_escritura: t('SALES.PENDING_CHARGE.PAYMENT_PERIODS.ATO_ESCRITURA'),
  transferencia_registros: t(
    'SALES.PENDING_CHARGE.PAYMENT_PERIODS.TRANSFERENCIA_REGISTROS'
  ),
}));
const methodLabels = computed(() => ({
  especie: t('SALES.PENDING_CHARGE.PAYMENT_METHODS.ESPECIE'),
  deposito: t('SALES.PENDING_CHARGE.PAYMENT_METHODS.DEPOSITO'),
  ted_doc: t('SALES.PENDING_CHARGE.PAYMENT_METHODS.TED_DOC'),
  pix: t('SALES.PENDING_CHARGE.PAYMENT_METHODS.PIX'),
  cheque: t('SALES.PENDING_CHARGE.PAYMENT_METHODS.CHEQUE'),
}));
const periodLabel = computed(
  () =>
    periodLabels.value[installment.value.periodo_pagamento] ||
    t('SALES.PENDING_CHARGE.EMPTY_VALUE')
);
const paymentLabel = computed(
  () =>
    methodLabels.value[commission.value.forma_pagamento] ||
    t('SALES.PENDING_CHARGE.EMPTY_VALUE')
);
const facts = computed(() => [
  {
    id: 'installment',
    label: t('SALES.PENDING_CHARGE.SUMMARY.INSTALLMENT_VALUE'),
    value: formatMoney(installment.value.valor_parcela),
  },
  {
    id: 'period',
    label: t('SALES.PENDING_CHARGE.SUMMARY.PAYMENT_PERIOD'),
    value: periodLabel.value,
  },
  {
    id: 'gross',
    label: t('SALES.PENDING_CHARGE.SUMMARY.GROSS_COMMISSION'),
    value: formatMoney(commission.value.valor_comissao_total),
  },
  {
    id: 'net',
    label: t('SALES.PENDING_CHARGE.SUMMARY.NET_COMMISSION'),
    value: formatMoney(commission.value.valor_comissao_liquida),
  },
  {
    id: 'payment',
    label: t('SALES.PENDING_CHARGE.SUMMARY.PAYMENT_METHOD'),
    value: paymentLabel.value,
  },
]);
</script>

<template>
  <LaisCard
    :title="t('SALES.PENDING_CHARGE.SUMMARY.TITLE')"
    prefix-icon="i-lucide-circle-dollar-sign"
  >
    <template #suffix>
      <span
        v-if="commission.tipo === 'PARCELADO'"
        class="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800"
      >
        {{
          t('SALES.PENDING_CHARGE.SUMMARY.INSTALLMENT_NUMBER', {
            number: installment.numero_parcela,
          })
        }}
      </span>
    </template>

    <dl class="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
      <div v-for="fact in facts" :key="fact.id">
        <dt
          class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
        >
          {{ fact.label }}
        </dt>
        <dd class="mt-1 text-sm font-semibold text-lais-text">
          {{ fact.value }}
        </dd>
      </div>
      <div>
        <dt
          class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
        >
          {{ t('SALES.PENDING_CHARGE.SUMMARY.TYPE') }}
        </dt>
        <dd class="mt-2 flex flex-wrap gap-2">
          <span
            class="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-800"
          >
            {{ commission.tipo || t('SALES.PENDING_CHARGE.EMPTY_VALUE') }}
          </span>
          <span
            v-if="Number(commission.quantidade_parcelas) > 1"
            class="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-800"
          >
            {{
              t('SALES.PENDING_CHARGE.SUMMARY.INSTALLMENT_COUNT', {
                number: commission.quantidade_parcelas,
              })
            }}
          </span>
        </dd>
      </div>
    </dl>

    <div
      class="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-lais-border-soft pt-4"
    >
      <p
        v-if="!installment.planilha_id"
        class="flex items-center gap-2 rounded-lais bg-amber-50 px-3 py-2 text-sm text-amber-800"
      >
        <span class="i-lucide-triangle-alert size-4" aria-hidden="true" />
        {{ t('SALES.PENDING_CHARGE.SUMMARY.WAITING_SUPPORT') }}
      </p>
      <span v-else />
      <LaisPrimaryButton
        variant="link"
        icon="i-lucide-download"
        :disabled="!canDownload"
        :is-loading="downloading"
        @click="emit('download')"
      >
        {{ t('SALES.PENDING_CHARGE.SUMMARY.VIEW_SHEET') }}
      </LaisPrimaryButton>
    </div>
  </LaisCard>
</template>
